import type { Key, ReactNode } from 'react';
import type { TableProps as TableNewProps } from './Table';
import type { TableCellProps as TableCellNewProps } from './TableCell';
import type { TableColProps as TableColNewProps } from './TableCol';
import type { TableRowProps as TableRowNewProps } from './TableRow';
import type { DataTableInternalProps, DataTableProps as DataTableNewProps } from './types';
import type { VirtualizedDataTableProps as VirtualizedDataTableNewProps } from './VirtualizedDataTable';

type NewDataTableProp =
  | 'sortKey'
  | 'hasSelectAll'
  | 'emptyContent'
  | 'emptyPadding'
  | 'resizable'
  | 'hasCellHoverStyle'
  | 'hasCellDisabledStyle'
  | 'hasExpandButton'
  | 'shouldRenderRowDivider'
  | 'getRowKey'
  | 'getRowSelectionKey'
  | 'onSortChange'
  | 'onSelectedRowsChange'
  | 'onExpandedRowsChange';

export type OldDataTableProps<DataType> = Omit<DataTableNewProps<DataType>, NewDataTableProp> & {
  /** @deprecated sortKey를 사용해주세요. */ sortBy?: DataTableNewProps<DataType>['sortKey'];
  /** @deprecated hasSelectAll을 사용해주세요. */ hideSelectAll?: boolean;
  /** @deprecated emptyContent를 사용해주세요. */ noDataText?: ReactNode;
  /** @deprecated emptyPadding을 사용해주세요. */ noDataPadding?: DataTableNewProps<DataType>['emptyPadding'];
  /** @deprecated resizable을 사용해주세요. */ resize?: DataTableNewProps<DataType>['resizable'];
  /** @deprecated hasCellHoverStyle을 사용해주세요. */ cellHoverStyle?: DataTableNewProps<DataType>['hasCellHoverStyle'];
  /** @deprecated hasCellDisabledStyle을 사용해주세요. */ cellDisabledStyle?: DataTableNewProps<DataType>['hasCellDisabledStyle'];
  /** @deprecated hasExpandButton을 사용해주세요. */ expandableButton?: DataTableNewProps<DataType>['hasExpandButton'];
  /** @deprecated shouldRenderRowDivider를 사용해주세요. */ dividerRows?: (row: DataType, index: number) => boolean;
  /** @deprecated getRowKey를 사용해주세요. */ rowKeyExtractor?: (row: DataType, index: number) => Key;
  /** @deprecated getRowSelectionKey를 사용해주세요. */ rowSelectionKeyExtractor?: (
    row: DataType,
    index: number,
  ) => Key;
  /** @deprecated onSortChange를 사용해주세요. */ onSortBy?: DataTableNewProps<DataType>['onSortChange'];
  /** @deprecated onSelectedRowsChange를 사용해주세요. */ onSelectRows?: DataTableNewProps<DataType>['onSelectedRowsChange'];
  /** @deprecated onExpandedRowsChange를 사용해주세요. */ onExpandRows?: DataTableNewProps<DataType>['onExpandedRowsChange'];
};

export function convertOldDataTableProps<DataType>(
  props: OldDataTableProps<DataType>,
): DataTableInternalProps<DataType> {
  const {
    sortBy,
    hideSelectAll,
    noDataText,
    noDataPadding,
    resize,
    cellHoverStyle,
    cellDisabledStyle,
    expandableButton,
    dividerRows,
    rowKeyExtractor,
    rowSelectionKeyExtractor,
    onSortBy,
    onSelectRows,
    onExpandRows,
    ...restProps
  } = props;
  return {
    ...restProps,
    sortKey: sortBy,
    hasSelectAll: hideSelectAll == null ? undefined : !hideSelectAll,
    emptyContent: noDataText,
    emptyPadding: noDataPadding,
    resizable: resize,
    hasCellHoverStyle: cellHoverStyle,
    hasCellDisabledStyle: cellDisabledStyle,
    hasExpandButton: expandableButton,
    shouldRenderRowDivider: dividerRows,
    getRowKey: rowKeyExtractor,
    getRowSelectionKey: rowSelectionKeyExtractor,
    onSortChange: onSortBy,
    onSelectedRowsChange: onSelectRows,
    onExpandedRowsChange: onExpandRows,
  };
}

export type OldTableProps = Omit<TableNewProps, 'resizable'> & {
  /** @deprecated resizable을 사용해주세요. */
  resize?: TableNewProps['resizable'];
};

export function convertOldTableProps(props: OldTableProps): TableNewProps {
  const { resize, ...restProps } = props;
  return { ...restProps, resizable: resize };
}

type NewTableCellProp =
  | 'fixedColumnWidth'
  | 'empty'
  | 'resizable'
  | 'lastRow'
  | 'hasDisabledStyle'
  | 'lastColumn'
  | 'truncate'
  | 'wrapContent';

export type OldTableCellProps = Omit<TableCellNewProps, NewTableCellProp> & {
  /** @deprecated fixedColumnWidth를 사용해주세요. */ fixedColWidth?: TableCellNewProps['fixedColumnWidth'];
  /** @deprecated empty를 사용해주세요. */ isEmpty?: TableCellNewProps['empty'];
  /** @deprecated resizable을 사용해주세요. */ resize?: TableCellNewProps['resizable'];
  /** @deprecated lastRow를 사용해주세요. */ isRowLast?: TableCellNewProps['lastRow'];
  /** @deprecated hasDisabledStyle을 사용해주세요. */ cellDisabledStyle?: TableCellNewProps['hasDisabledStyle'];
  /** @deprecated lastColumn을 사용해주세요. */ isColumnLast?: TableCellNewProps['lastColumn'];
  /** @deprecated truncate를 사용해주세요. */ isTruncate?: TableCellNewProps['truncate'];
  /** @deprecated wrapContent를 사용해주세요. */ isHugeContents?: TableCellNewProps['wrapContent'];
};

export function convertOldTableCellProps(props: OldTableCellProps): TableCellNewProps {
  const {
    fixedColWidth,
    isEmpty,
    resize,
    isRowLast,
    cellDisabledStyle,
    isColumnLast,
    isTruncate,
    isHugeContents,
    ...restProps
  } = props;
  return {
    ...restProps,
    fixedColumnWidth: fixedColWidth,
    empty: isEmpty,
    resizable: resize,
    lastRow: isRowLast,
    hasDisabledStyle: cellDisabledStyle,
    lastColumn: isColumnLast,
    truncate: isTruncate,
    wrapContent: isHugeContents,
  };
}

type NewTableColProp = 'resizable' | 'verticalAlign' | 'sortIndicatorProps' | 'hasRightBorder';

export type OldTableColProps = Omit<TableColNewProps, NewTableColProp> & {
  /** @deprecated resizable을 사용해주세요. */ resize?: TableColNewProps['resizable'];
  /** @deprecated verticalAlign을 사용해주세요. */ valign?: TableColNewProps['verticalAlign'];
  /** @deprecated sortIndicatorProps를 사용해주세요. */ sortProps?: TableColNewProps['sortIndicatorProps'];
  /** @deprecated hasRightBorder를 사용해주세요. */ showRightBorder?: TableColNewProps['hasRightBorder'];
};

export function convertOldTableColProps(props: OldTableColProps): TableColNewProps {
  const { resize, valign, sortProps, showRightBorder, ...restProps } = props;
  return {
    ...restProps,
    resizable: resize,
    verticalAlign: valign,
    sortIndicatorProps: sortProps,
    hasRightBorder: showRightBorder,
  };
}

export type OldTableRowProps = Omit<TableRowNewProps, 'empty' | 'hasHoverStyle'> & {
  /** @deprecated empty를 사용해주세요. */ isEmpty?: TableRowNewProps['empty'];
  /** @deprecated hasHoverStyle을 사용해주세요. */ cellHoverStyle?: TableRowNewProps['hasHoverStyle'];
};

export function convertOldTableRowProps(props: OldTableRowProps): TableRowNewProps {
  const { isEmpty, cellHoverStyle, ...restProps } = props;
  return { ...restProps, empty: isEmpty, hasHoverStyle: cellHoverStyle };
}

export type OldVirtualizedDataTableProps<DataType> = Omit<OldDataTableProps<DataType>, 'stickyHeader'> & {
  /**
   * 테이블 박스 내부 스크롤에 헤더를 고정할지 여부
   *
   * @default true
   */
  stickyHeader?: boolean;
  /** @deprecated estimateRowSize를 사용해주세요. */
  estimateSize: number;
};

export function convertOldVirtualizedDataTableProps<DataType>(
  props: OldVirtualizedDataTableProps<DataType>,
): VirtualizedDataTableNewProps<DataType> {
  const { estimateSize, stickyHeader, ...restProps } = props;
  return {
    ...convertOldDataTableProps(restProps),
    stickyHeader,
    estimateRowSize: estimateSize,
  };
}
